import React, { useState } from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  IconButton,
  Strong,
  Spinner,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  CameraIcon,
  PlayIcon,
  TickIcon,
  TrashIcon,
  Dialog,
  Image,
  toaster
} from 'evergreen-ui';
import { CommentSection } from '../../Comments';
import { groupDomainsBySecondLevel, getSeverityBackgroundColor } from '../utils';

export const DomainsTab = ({
  domains,
  isLoading,
  hasMore,
  searchQuery,
  commentCounts,
  expandedComments,
  manuallyCollapsedComments,
  expandedDomainGroups,
  toggleCommentsExpansion,
  collapseComments,
  toggleDomainGroupExpansion,
  actionLoading,
  handleAction,
  openEntityOverview,
  currentProject,
  currentUser
}) => {
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const domainGroups = groupDomainsBySecondLevel(domains);
  const groupKeys = Object.keys(domainGroups);

  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>
          Domains {domains.length > 0 && `(${domains.length} shown${!searchQuery && hasMore ? ', more available' : ''})`}
        </Heading>
      </Pane>
      
      {domains.length === 0 && !isLoading ? (
        <Card background="tint1" padding={32} textAlign="center">
          <Text color="muted">
            {searchQuery ? 'No domains match your search' : 'No domains found'}
          </Text>
        </Card>
      ) : (
        <Pane>
          {groupKeys.map(secondLevelDomain => {
            const groupDomains = domainGroups[secondLevelDomain];
            const isExpanded = expandedDomainGroups[secondLevelDomain];
            const newCount = groupDomains.filter(d => d.is_new).length;
            const checkedCount = groupDomains.filter(d => d.is_checked).length;
            const highestSeverity = groupDomains.reduce((highest, domain) => {
              const severityOrder = { 'critical': 5, 'high': 4, 'medium': 3, 'low': 2, 'info': 1, 'none': 0 };
              const currentSeverity = severityOrder[domain.severity] || 0;
              const highestSeverity = severityOrder[highest] || 0;
              return currentSeverity > highestSeverity ? domain.severity : highest;
            }, 'none');

            return (
              <Card
                key={secondLevelDomain}
                background={newCount > 0 ? "#E6F4EA" : "white"}
                border={newCount > 0 ? "2px solid #47B881" : "1px solid #E6E8F0"}
                padding={16}
                marginBottom={8}
                elevation={0}
              >
                <Pane 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between"
                  cursor="pointer"
                  onClick={() => toggleDomainGroupExpansion(secondLevelDomain)}
                >
                  <Pane display="flex" alignItems="center" flex={1}>
                    <IconButton
                      icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                      marginRight={8}
                      height={24}
                    />
                    <Strong size={500} marginRight={12}>{secondLevelDomain}</Strong>
                    
                    <Pane display="flex" gap={8} alignItems="center">
                      <Badge backgroundColor="#F7F8FA" color="default">
                        {groupDomains.length} {groupDomains.length === 1 ? 'subdomain' : 'subdomains'}
                      </Badge>
                      
                      {highestSeverity !== 'none' && (
                        <Badge
                          backgroundColor={getSeverityBackgroundColor(highestSeverity)}
                          color={highestSeverity === 'critical' ? 'red' : 'default'}
                        >
                          {highestSeverity}
                        </Badge>
                      )}
                      
                      {newCount > 0 && (
                        <Badge backgroundColor="#47B881" color="white">
                          {newCount} NEW
                        </Badge>
                      )}
                      
                      {checkedCount > 0 && (
                        <Badge backgroundColor="#999999" color="white">
                          {checkedCount} CHECKED
                        </Badge>
                      )}
                    </Pane>
                    
                    {groupDomains.length > 0 && groupDomains.some(d => d.resolved_ips && d.resolved_ips.length > 0) && (
                      <Pane display="flex" alignItems="center" marginLeft={16}>
                        <ArrowRightIcon size={12} marginRight={4} color="muted" />
                        <Text size={300} color="muted">
                          {Array.from(new Set(groupDomains.flatMap(d => d.resolved_ips || []))).slice(0, 3).join(', ')}
                          {Array.from(new Set(groupDomains.flatMap(d => d.resolved_ips || []))).length > 3 && ' ...'}
                        </Text>
                      </Pane>
                    )}
                  </Pane>
                </Pane>

                {isExpanded && (
                  <Pane marginTop={16} marginLeft={32}>
                    <Pane display="flex" flexDirection="column" gap={8}>
                      {groupDomains.map(domain => (
                        <React.Fragment key={domain.id}>
                          <Card background="tint1" padding={12} elevation={0}>
                            <Pane display="flex" alignItems="flex-start" justifyContent="space-between">
                              <Pane flex={1}>
                                <Pane display="flex" alignItems="center" marginBottom={8}>
                                  <Strong size={400} marginRight={8}>{domain.name}</Strong>
                                  <Badge
                                    backgroundColor={getSeverityBackgroundColor(domain.severity)}
                                    color={domain.severity === 'critical' ? 'red' : 'default'}
                                    marginRight={8}
                                  >
                                    {domain.severity || 'none'}
                                  </Badge>
                                  {domain.is_new && (
                                    <Badge backgroundColor="#47B881" color="white" marginRight={8}>
                                      NEW
                                    </Badge>
                                  )}
                                  {domain.is_checked && (
                                    <Badge backgroundColor="#999999" color="white">
                                      CHECKED
                                    </Badge>
                                  )}
                                </Pane>
                                
                                {domain.sources?.length > 0 && (
                                  <Pane marginBottom={8}>
                                    <Text size={300} color="muted" marginBottom={4}>Sources:</Text>
                                    <Pane display="flex" flexWrap="wrap" gap={4}>
                                      {domain.sources.map(source => (
                                        <Badge key={source} backgroundColor="#F7F8FA" color="default" size="small">
                                          {source}
                                        </Badge>
                                      ))}
                                    </Pane>
                                  </Pane>
                                )}
                                
                                {domain.resolved_ips && domain.resolved_ips.length > 0 && (
                                  <Pane marginBottom={8}>
                                    <Pane display="flex" alignItems="center">
                                      <Text size={300} color="muted" marginRight={8}>Resolves to:</Text>
                                      <ArrowRightIcon size={12} marginRight={4} color="muted" />
                                      <Text size={300} color="default">
                                        {domain.resolved_ips.join(', ')}
                                      </Text>
                                    </Pane>
                                  </Pane>
                                )}
                                
                                {domain.screenshot && domain.screenshot.thumbnail_url && (
                                  <Pane marginTop={8} marginBottom={8}>
                                    <img 
                                      src={domain.screenshot.thumbnail_url} 
                                      alt={`Screenshot of ${domain.name}`}
                                      onClick={() => setSelectedScreenshot(domain.screenshot)}
                                      style={{
                                        maxWidth: '200px',
                                        maxHeight: '120px',
                                        borderRadius: '4px',
                                        border: '1px solid #E6E8F0',
                                        cursor: 'pointer',
                                        objectFit: 'contain'
                                      }}
                                    />
                                  </Pane>
                                )}
                              </Pane>
                              
                              <Pane display="flex" flexDirection="column" gap={4}>
                                <Button
                                  size="small"
                                  appearance="minimal"
                                  iconBefore={ArrowRightIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEntityOverview('domain', domain.id, { name: domain.name, type: 'domain' });
                                  }}
                                >
                                  View Details
                                </Button>
                                <Button
                                  size="small"
                                  appearance="minimal"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://${domain.name}`, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  Open ↗
                                </Button>
                                <Button
                                  size="small"
                                  appearance="minimal"
                                  iconBefore={expandedComments[`domain_id_${domain.id}`] ? ChevronDownIcon : ChevronRightIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCommentsExpansion(`domain_id_${domain.id}`);
                                  }}
                                >
                                  Comments
                                  {commentCounts[`domain_id_${domain.id}`] > 0 && (
                                    <Badge marginLeft={8} backgroundColor="#1070CA" color="white">
                                      {commentCounts[`domain_id_${domain.id}`]}
                                    </Badge>
                                  )}
                                </Button>
                                <Button
                                  size="small"
                                  iconBefore={CameraIcon}
                                  isLoading={actionLoading[`screenshot_domain_${domain.id}`]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction('screenshot', 'domain', domain.id, {
                                      name: domain.name
                                    });
                                  }}
                                >
                                  Screenshot
                                </Button>
                                <Button
                                  size="small"
                                  iconBefore={PlayIcon}
                                  isLoading={actionLoading[`ffuf_domain_${domain.id}`]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction('ffuf', 'domain', domain.id, {
                                      name: domain.name
                                    });
                                  }}
                                >
                                  FFUF
                                </Button>
                                <Button
                                  size="small"
                                  iconBefore={TickIcon}
                                  intent={domain.is_checked ? "success" : "default"}
                                  isLoading={actionLoading[`mark_checked_domain_${domain.id}`]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction('mark_checked', 'domain', domain.id, {
                                      name: domain.name
                                    });
                                  }}
                                >
                                  {domain.is_checked ? 'Uncheck' : 'Check'}
                                </Button>
                                <IconButton
                                  icon={TrashIcon}
                                  intent="danger"
                                  size="small"
                                  appearance="minimal"
                                  isLoading={actionLoading[`delete_domain_${domain.id}`]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete domain ${domain.name}?`)) {
                                      handleAction('delete', 'domain', domain.id);
                                    }
                                  }}
                                />
                              </Pane>
                            </Pane>
                          </Card>
                          
                          <CommentSection
                            projectId={currentProject.id}
                            entityType="domain_id"
                            entityId={domain.id}
                            currentUserId={currentUser?.id}
                            compact={true}
                            forceShow={expandedComments[`domain_id_${domain.id}`] || false}
                            manuallyCollapsed={manuallyCollapsedComments[`domain_id_${domain.id}`] || false}
                            onAutoExpand={(entityId) => {
                              toggleCommentsExpansion(entityId);
                            }}
                            onCollapse={collapseComments}
                          />
                        </React.Fragment>
                      ))}
                    </Pane>
                  </Pane>
                )}
              </Card>
            );
          })}
          
          {isLoading && (
            <Pane display="flex" justifyContent="center" padding={16}>
              <Spinner />
            </Pane>
          )}
        </Pane>
      )}
      
      {selectedScreenshot && (
        <Dialog
          isShown={!!selectedScreenshot}
          title="Screenshot"
          onCloseComplete={() => setSelectedScreenshot(null)}
          hasFooter={false}
          width="90vw"
          maxWidth="1200px"
        >
          <Pane padding={16}>
            <Image
              src={selectedScreenshot.screenshot_url || selectedScreenshot.thumbnail_url}
              alt="Full screenshot"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          </Pane>
        </Dialog>
      )}
    </Pane>
  );
};

